/**
 * Selectors are constructed by receiving a list of classNames applied to
 * DOM-elements. These classNames will be transformed to QuerySelectors by our
 * makeSelectors() method.
 */
type ClassName = string;

/**
 * A QuerySelector represents a selector that can be used to access a
 * particular DOM-Element, e.g. by using document.querySelector(QuerySelector).
 */
type QuerySelector = (() => string) & { className: string };

/**
 * Makes an object of selectors that can be used to access DOM-elements related
 * to the passed classNames imperatively without the need to manually transform
 * classNames into selectors.
 *
 * This can be used to both avoid manual work and at the same time it allows
 * components to create an object of selectors based on the already defined
 * classes (using linaria.css``) scoped to the given component.
 */
export function makeSelectors(selector: ClassName): QuerySelector;
export function makeSelectors<T extends string>(
  selectors: Record<T, ClassName>,
): Record<T, QuerySelector>;
export function makeSelectors<T extends string>(
  selectors: ClassName | Record<T, ClassName>,
): QuerySelector | Record<T, QuerySelector> {
  if (typeof selectors === "string") {
    return Object.assign(() => `.${selectors}`, {
      className: selectors,
    });
  }

  return Object.entries(selectors).reduce(
    (acc, [rawSelector, className]) => {
      // @ts-expect-error TypeScript cannot correctly infer that `typeof
      // selector` =T
      acc[rawSelector] = Object.assign(() => `.${className}`, {
        className,
      });

      return acc;
    },
    {} as Record<T, QuerySelector>,
  );
}
